import { publicDomains } from '@wagenow-ewa/common/dist/src/constants/regionalConstants'
import { APP_LANGUAGES, DEFAULT_APP_LANGUAGE } from './enums'

/**
 * Language utility functions for internationalization support
 */

/**
 * Get all available languages from APP_LANGUAGES enum
 */
export const getAvailableLanguages = (): string[] => {
	return Object.values(APP_LANGUAGES)
}

/**
 * Check if a language code is supported by the application
 */
export const isLanguageSupported = (language: string): boolean => {
	return getAvailableLanguages().includes(language)
}

/**
 * Detect browser language and return supported language or fallback
 * Maps common browser language codes to supported languages
 */
export const detectBrowserLanguage = (): string => {
	// Get browser language, fallback to 'en' if not available
	const browserLanguage = navigator.language || navigator.languages?.[0] || 'en'

	// Extract language code (e.g., 'en-US' -> 'en', 'sk-SK' -> 'sk')
	const languageCode = browserLanguage.split('-')[0].toLowerCase()

	// Map common language codes to supported languages
	const languageMap: Record<string, string> = {
		en: APP_LANGUAGES.EN,
		sk: APP_LANGUAGES.SK,
		cs: APP_LANGUAGES.SK // Czech users might prefer Slovak
	}

	// Return mapped language if supported, otherwise default
	const mappedLanguage = languageMap[languageCode]
	return isLanguageSupported(mappedLanguage) ? mappedLanguage : DEFAULT_APP_LANGUAGE
}

/**
 * Extract language code from URL pathname
 * Returns null if no valid language prefix is found
 */
export const extractLanguageFromPath = (pathname: string): string | null => {
	// Remove leading slash and split path segments
	const segments = pathname.replace(/^\//, '').split('/')
	const potentialLanguage = segments[0]

	// Check if first segment is a valid language code
	if (potentialLanguage && isLanguageSupported(potentialLanguage)) {
		return potentialLanguage
	}

	return null
}

/**
 * Remove language prefix from pathname
 * Returns the path without language prefix
 */
export const removeLanguageFromPath = (pathname: string): string => {
	const language = extractLanguageFromPath(pathname)

	if (language) {
		// Remove language prefix and ensure path starts with /
		const pathWithoutLanguage = pathname.replace(new RegExp(`^/${language}`), '') || '/'
		return pathWithoutLanguage.startsWith('/') ? pathWithoutLanguage : `/${pathWithoutLanguage}`
	}

	return pathname
}

/**
 * Add language prefix to pathname
 * Ensures proper formatting with leading slash
 */
export const addLanguageToPath = (pathname: string, language: string): string => {
	if (!isLanguageSupported(language)) {
		throw new Error(`Unsupported language: ${language}`)
	}

	// Remove any existing language prefix first
	const cleanPath = removeLanguageFromPath(pathname)

	// Add new language prefix
	return `/${language}${cleanPath === '/' ? '' : cleanPath}`
}

/**
 * Get language preference from localStorage
 * Returns null if no preference is stored or if stored language is not supported
 */
export const getStoredLanguagePreference = (): string | null => {
	try {
		const storedLanguage = localStorage.getItem('preferred-language')
		return storedLanguage && isLanguageSupported(storedLanguage) ? storedLanguage : null
	} catch (error) {
		// localStorage might not be available (e.g., in SSR or private browsing)
		console.warn('Failed to read language preference from localStorage:', error)
		return null
	}
}

/**
 * Store language preference in localStorage
 */
export const storeLanguagePreference = (language: string): void => {
	if (!isLanguageSupported(language)) {
		throw new Error(`Cannot store unsupported language: ${language}`)
	}

	try {
		localStorage.setItem('preferred-language', language)
	} catch (error) {
		// localStorage might not be available (e.g., in SSR or private browsing)
		console.warn('Failed to store language preference in localStorage:', error)
	}
}

/**
 * Determine the initial language based on URL, stored preference, and browser detection
 * Priority: URL > localStorage > browser detection > default
 * Note: Onboarding data language will be handled separately after data loads
 */
export const determineInitialLanguage = (pathname: string): string => {
	// 1. Check URL for language prefix (highest priority)
	const urlLanguage = extractLanguageFromPath(pathname)
	if (urlLanguage) {
		return urlLanguage
	}

	// 2. Check localStorage for saved preference
	const storedLanguage = getStoredLanguagePreference()
	if (storedLanguage) {
		return storedLanguage
	}

	// 3. Detect browser language
	const browserLanguage = detectBrowserLanguage()
	if (browserLanguage) {
		return browserLanguage
	}

	// 4. Fallback to default language
	return DEFAULT_APP_LANGUAGE
}

/**
 * Validate and normalize language code
 * Returns the language if valid, otherwise returns default language
 */
export const validateLanguage = (language: string | null | undefined): string => {
	if (!language || !isLanguageSupported(language)) {
		return DEFAULT_APP_LANGUAGE
	}
	return language
}

/**
 * Get dayjs locale string for the given language
 * Maps application language codes to dayjs locale identifiers
 */
export const getDayjsLocale = (language: string): string => {
	const localeMap: Record<string, string> = {
		[APP_LANGUAGES.EN]: 'en-gb',
		[APP_LANGUAGES.SK]: 'sk'
	}

	return localeMap[language] || localeMap[DEFAULT_APP_LANGUAGE]
}

/**
 * Get localized URLs for legal documents based on company language
 * Returns appropriate URLs for Terms & Conditions and Privacy Policy
 */
export const getLocalizedUrls = (language = 'sk') => {
	if (language === 'en') {
		return {
			termsUrl: `${publicDomains.uk}/gtc/`,
			privacyUrl: `${publicDomains.uk}/privacy-policy/`
		}
	}
	// Default Slovak URLs
	return {
		termsUrl: `${publicDomains.sk}/vop`,
		privacyUrl: `${publicDomains.sk}/sk/gdpr`
	}
}

/**
 * Check if the current path has a language prefix
 */
export const hasLanguagePrefix = (pathname: string): boolean => {
	return extractLanguageFromPath(pathname) !== null
}

/**
 * Generate a localized path with language prefix
 * If path already has a language prefix, it will be replaced
 */
export const getLocalizedPath = (path: string, language: string): string => {
	return addLanguageToPath(path, language)
}
